/**
 * Wo ein Gerät im Bestand hängt (#785).
 *
 * Ein Gerät trägt Etage und Raum nur als Ids (`Asset.floor`, `Asset.space`); das Gebäude hängt
 * an der Etage (`Floor.building`), und ein Raum kennt seine Etage (`Space.floor`). Für die
 * Bestandsliste zählt aber der Name — und vor allem die Antwort auf die Frage, ob überhaupt ein
 * Standort vergeben ist: Geräte, die in der App ohne Etagenplan registriert wurden (#720), haben
 * keinen, tauchen in keinem Etagen- oder Raumplan auf, und genau die soll die Liste finden.
 *
 * Aufgelöst wird in einem Rutsch für eine ganze Liste: Räume, dann Etagen, dann Gebäude. Jeder
 * Schritt ist einzeln abgesichert — fällt einer aus, fehlt nur sein Namensteil, die Liste bleibt
 * benutzbar und zeigt an dieser Stelle die Id.
 */

import { Building, Floor, Space, from, is } from "@the-real-insight/tri-model";

import { userAPI } from "../UserAPI";
import { entityIdAsString } from "./buildingLookup";

export interface AssetLocationParts {
  building?: string;
  floor?: string;
  space?: string;
}

export interface LocatedAsset {
  _id?: any;
  floor?: any;
  space?: any;
}

/**
 * Ob ein Gerät einer Etage oder einem Raum zugeordnet ist. Ohne beides steht es in keinem
 * Etagenplan — das ist der Zustand, den die Bestandsliste mit »ohne Standort« meint.
 */
export const hasLocation = (asset: LocatedAsset | null | undefined): boolean =>
  !!(entityIdAsString(asset?.floor) || entityIdAsString(asset?.space));

const uniqueIds = (values: Array<string | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => !!value)));

const queryByIds = async (type: any, ids: string[]): Promise<any[]> => {
  if (ids.length === 0) return [];

  const rows = await userAPI.query(from(type).where(is("_id").in(ids) as any));

  return Array.isArray(rows) ? rows : [];
};

const byId = (rows: any[]): Record<string, any> =>
  rows.reduce((map: Record<string, any>, row: any) => {
    const id = entityIdAsString(row?._id);
    if (id) map[id] = row;

    return map;
  }, {});

/**
 * Namen für die Standorte aller übergebenen Geräte, je Gerät unter dessen `_id`. Geräte ohne
 * Etage und Raum bekommen keinen Eintrag; ein Teil fehlt, wenn er nicht auflösbar ist.
 */
export async function resolveAssetLocations(
  assets: LocatedAsset[],
): Promise<Record<string, AssetLocationParts>> {
  const spaceIds = uniqueIds(assets.map((asset) => entityIdAsString(asset.space)));
  const spaces = await queryByIds(Space.type, spaceIds).catch(() => []);
  const spacesById = byId(spaces);

  // Die Etage steht am Gerät — oder, wenn nur der Raum vergeben ist, am Raum.
  const floorIds = uniqueIds([
    ...assets.map((asset) => entityIdAsString(asset.floor)),
    ...spaces.map((space: any) => entityIdAsString(space?.floor)),
  ]);
  const floors = await queryByIds(Floor.type, floorIds).catch(() => []);
  const floorsById = byId(floors);

  const buildingIds = uniqueIds(floors.map((floor: any) => entityIdAsString(floor?.building)));
  const buildingsById = byId(await queryByIds(Building.type, buildingIds).catch(() => []));

  return assets.reduce((result: Record<string, AssetLocationParts>, asset) => {
    const assetId = entityIdAsString(asset?._id);
    if (!assetId || !hasLocation(asset)) return result;

    const spaceId = entityIdAsString(asset.space);
    const space = spaceId ? spacesById[spaceId] : undefined;
    const floorId = entityIdAsString(asset.floor) ?? entityIdAsString(space?.floor);
    const floor = floorId ? floorsById[floorId] : undefined;
    const buildingId = entityIdAsString(floor?.building);

    result[assetId] = {
      building: buildingId ? buildingsById[buildingId]?.name : undefined,
      floor: floor?.name,
      space: space?.name,
    };

    return result;
  }, {});
}

/**
 * Anzeigetext der Kette Gebäude · Etage · Raum; nicht auflösbare Teile fallen auf ihre Id
 * zurück. `undefined` für ein Gerät ohne Standort — die Anzeige entscheidet, was sie dann sagt.
 */
export function describeAssetLocation(
  asset: LocatedAsset,
  parts: AssetLocationParts | undefined,
): string | undefined {
  if (!hasLocation(asset)) return undefined;

  const floorId = entityIdAsString(asset.floor);
  const spaceId = entityIdAsString(asset.space);

  const chain = [
    parts?.building,
    floorId || parts?.floor ? (parts?.floor ?? floorId) : undefined,
    spaceId ? (parts?.space ?? spaceId) : undefined,
  ].filter(Boolean);

  return chain.join(" · ");
}
